import React, { useState, useMemo } from 'react';
import './JSONVisualizer.css';
import { CheckIcon, DocumentIcon, FolderIcon, BarChartIcon } from '@bizfirst/common-react';

export interface JSONVisualizerProps {
  data: any;
  className?: string;
  title?: React.ReactNode;
}

type TabType = 'tree' | 'json' | 'stats';

interface JSONStats {
  totalKeys: number;
  totalValues: number;
  maxDepth: number;
  totalSize: number;
  dataTypes: Record<string, number>;
  stringCount: number;
  numberCount: number;
  booleanCount: number;
  nullCount: number;
  arrayCount: number;
  objectCount: number;
}

const JSON_TYPE_COLORS: Record<string, string> = {
  string: 'var(--color-success, #22c55e)',
  number: 'var(--color-info, #3b82f6)',
  boolean: 'var(--color-warning, #f59e0b)',
  null: 'var(--color-danger, #ef4444)',
  array: 'var(--color-primary, #8b5cf6)',
  object: 'var(--color-secondary, #06b6d4)',
  undefined: 'var(--color-text-tertiary, #9ca3af)',
};

function computeJSONStats(data: any): JSONStats {
  const stats: JSONStats = {
    totalKeys: 0,
    totalValues: 0,
    maxDepth: 0,
    totalSize: JSON.stringify(data)?.length ?? 0,
    dataTypes: {},
    stringCount: 0,
    numberCount: 0,
    booleanCount: 0,
    nullCount: 0,
    arrayCount: 0,
    objectCount: 0,
  };

  function walk(val: any, depth: number) {
    if (depth > stats.maxDepth) stats.maxDepth = depth;
    if (val === null) {
      stats.nullCount++;
      stats.totalValues++;
      stats.dataTypes['null'] = (stats.dataTypes['null'] || 0) + 1;
    } else if (Array.isArray(val)) {
      stats.arrayCount++;
      stats.dataTypes['array'] = (stats.dataTypes['array'] || 0) + 1;
      val.forEach((item) => walk(item, depth + 1));
    } else if (typeof val === 'object') {
      stats.objectCount++;
      stats.dataTypes['object'] = (stats.dataTypes['object'] || 0) + 1;
      for (const key of Object.keys(val)) {
        stats.totalKeys++;
        walk(val[key], depth + 1);
      }
    } else {
      const t = typeof val;
      stats.totalValues++;
      stats.dataTypes[t] = (stats.dataTypes[t] || 0) + 1;
      if (t === 'string') stats.stringCount++;
      else if (t === 'number') stats.numberCount++;
      else if (t === 'boolean') stats.booleanCount++;
    }
  }

  walk(data, 0);
  return stats;
}

function useJSONStats(data: any): JSONStats {
  return useMemo(() => computeJSONStats(data), [data]);
}

const KNOWN_FIELDS = new Set([
  'id', 'messageId', 'timestamp', 'startTime', 'endTime', 'status', 'type', 'messageType',
  'topic', 'serverId', 'userId', 'user', 'groupName', 'description', 'created', 'modified',
  'name', 'email', 'phone', 'address', 'city', 'state', 'country', 'zipCode',
  'totalTime', 'processingTime', 'duration', 'count', 'size', 'version',
  'error', 'message', 'code', 'reason', 'details', 'metadata', 'context',
  'summary', 'title', 'body', 'content', 'text', 'data', 'payload',
]);

function TreeNode({ data, nodeKey, level = 0 }: { data: any; nodeKey: string; level?: number }): React.ReactElement {
  const [expanded, setExpanded] = useState(level < 2);
  const isKnownField = KNOWN_FIELDS.has(nodeKey);

  const isExpandable = data !== null && (typeof data === 'object');
  const isArray = Array.isArray(data);

  const getTypeColor = (type: string): string => {
    return JSON_TYPE_COLORS[type as keyof typeof JSON_TYPE_COLORS] || 'var(--color-text-tertiary, #9ca3af)';
  };

  const getType = (val: any): string => {
    if (val === null) return 'null';
    if (Array.isArray(val)) return 'array';
    return typeof val;
  };

  const type = getType(data);

  return (
    <div className="json-tree-node" style={{ marginLeft: `${level * 16}px` }}>
      <div className="json-node-header">
        {isExpandable && (
          <button
            className="json-expand-toggle"
            onClick={() => setExpanded(!expanded)}
            style={{ visibility: isExpandable ? 'visible' : 'hidden' }}
          >
            {expanded ? '▼' : '▶'}
          </button>
        )}
        {!isExpandable && <span style={{ width: '16px' }} />}

        <span className={`json-field-name ${isKnownField ? 'known-field' : ''}`}>{nodeKey}</span>

        {isKnownField && <span className="json-field-badge"><CheckIcon size={10} /> Known Field</span>}

        <span className="json-type-badge" style={{ backgroundColor: getTypeColor(type) }}>
          {type}
        </span>

        {!isExpandable && (
          <span className="json-field-value">
            {type === 'string' ? `"${String(data).substring(0, 50)}${String(data).length > 50 ? '...' : ''}"` : String(data)}
          </span>
        )}

        {isExpandable && (
          <span className="json-field-count">
            {isArray ? `${data.length} items` : `${Object.keys(data).length} fields`}
          </span>
        )}
      </div>

      {isExpandable && expanded && (
        <div className="json-node-children">
          {isArray ? (
            data.map((item: any, index: number) => (
              <TreeNode key={index} data={item} nodeKey={`[${index}]`} level={level + 1} />
            ))
          ) : (
            Object.entries(data).map(([key, value]: [string, any]) => (
              <TreeNode key={key} data={value} nodeKey={key} level={level + 1} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function JSONCodeViewer({ data }: { data: any }): React.ReactElement {
  const jsonString = JSON.stringify(data, null, 2);

  return (
    <div className="json-code-viewer">
      <pre className="json-code-block">
        <code>{jsonString}</code>
      </pre>
    </div>
  );
}

function StatsTab({ stats }: { stats: JSONStats }): React.ReactElement {
  return (
    <div className="json-stats-container">
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-label">Total Keys</div>
          <div className="stat-value">{stats.totalKeys}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Total Values</div>
          <div className="stat-value">{stats.totalValues}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Max Depth</div>
          <div className="stat-value">{stats.maxDepth}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Total Size</div>
          <div className="stat-value">{stats.totalSize}</div>
        </div>
      </div>

      <div className="stats-types">
        <h4>Data Types Distribution</h4>
        <div className="type-bars">
          {Object.entries(stats.dataTypes).map(([type, count]) => {
            const maxCount = Math.max(...Object.values(stats.dataTypes));
            const percentage = (count / maxCount) * 100;
            const color = JSON_TYPE_COLORS[type as keyof typeof JSON_TYPE_COLORS] || 'var(--color-text-tertiary)';

            return (
              <div key={type} className="type-bar">
                <div className="type-label">{type}</div>
                <div className="type-bar-container">
                  <div
                    className="type-bar-fill"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
                <div className="type-count">{count}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="stats-summary">
        <h4>Value Type Summary</h4>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Strings:</span>
            <span className="summary-value">{stats.stringCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Numbers:</span>
            <span className="summary-value">{stats.numberCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Booleans:</span>
            <span className="summary-value">{stats.booleanCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Nulls:</span>
            <span className="summary-value">{stats.nullCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Arrays:</span>
            <span className="summary-value">{stats.arrayCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Objects:</span>
            <span className="summary-value">{stats.objectCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function JSONVisualizer({ data, className = '', title }: JSONVisualizerProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<TabType>('tree');
  const [copied, setCopied] = useState(false);
  const stats = useJSONStats(data);

  const handleCopy = () => {
    const jsonString = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`json-visualizer ${className}`}>
      {title && <h3 className="json-visualizer-title">{title}</h3>}

      <div className="json-tabs">
        <button
          className={`json-tab-button ${activeTab === 'tree' ? 'active' : ''}`}
          onClick={() => setActiveTab('tree')}
        >
          <FolderIcon size={14} /> Tree View
        </button>
        <button
          className={`json-tab-button ${activeTab === 'json' ? 'active' : ''}`}
          onClick={() => setActiveTab('json')}
        >
          {} JSON
        </button>
        <button
          className={`json-tab-button ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <BarChartIcon size={14} /> Statistics
        </button>
        <button
          onClick={handleCopy}
          className="json-copy-button"
          title="Copy JSON to clipboard"
        >
          {copied ? <><CheckIcon size={14} /> Copied</> : <><DocumentIcon size={14} /> Copy</>}
        </button>
      </div>

      <div className="json-tab-content">
        {activeTab === 'tree' && (
          <div className="json-tree-container">
            {data && typeof data === 'object' ? (
              Object.entries(data).map(([key, value]) => <TreeNode key={key} data={value} nodeKey={key} />)
            ) : (
              <div className="json-placeholder">Invalid JSON data</div>
            )}
          </div>
        )}

        {activeTab === 'json' && <JSONCodeViewer data={data} />}

        {activeTab === 'stats' && <StatsTab stats={stats} />}
      </div>
    </div>
  );
}
